const REVEAL_SELECTOR = "[data-bv-reveal]"
const HERO_SELECTOR = "[data-bv-hero]"
const FADE_SELECTOR = "[data-bv-fade]"
const IN_CLASS = "bv-reveal--in"
const OBSERVER_OPTIONS = { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
const SWEEP_DELAY = 120
const HEADER_OFFSET = 56
const HERO_SHIFT = 70
const HERO_BLUR = 14
const FADE_SHIFT = 24
const FADE_BLUR = 10
const ORBIT_SELECTOR = "[data-bv-orbit]"
const ORBIT_OPTIONS = { rootMargin: "12% 0px" }
const ORBIT_DRIVE = 0.34
const ORBIT_FRICTION = 0.982
const ORBIT_EASE = 0.09
const ORBIT_REST = 0.0015
const ORBIT_COAST_DELAY = 140
const ORBIT_RING_RATIO = -0.55
const ORBIT_MIN_SCROLL = 0.4

let observer = null
let sweepTimer = null
let dissolveQueued = false
let heroItems = []
let fadeItems = []
let orbits = []
let orbitWatcher = null
let orbitFrame = null
let orbitLastY = 0
let motionAllowed = false

let clamp01 = n => Math.max(0, Math.min(1, n))

let revealNow = el => el.classList.add(IN_CLASS)

let isPending = el => !el.classList.contains(IN_CLASS)

let isDue = el => el.getBoundingClientRect().top < window.innerHeight * 0.92

let onIntersect = (entries, obs) => {
	entries.forEach(entry => {
		if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return
		revealNow(entry.target)
		obs.unobserve(entry.target)
	})
}

let sweep = () => {
	if (!observer) return
	document.querySelectorAll(REVEAL_SELECTOR).forEach(el => {
		if (!isPending(el) || !isDue(el)) return
		revealNow(el)
		observer.unobserve(el)
	})
}

let scheduleSweep = () => {
	window.clearTimeout(sweepTimer)
	sweepTimer = window.setTimeout(sweep, SWEEP_DELAY)
}

let observe = root => {
	let items = (root ?? document).querySelectorAll(REVEAL_SELECTOR)
	if (!items.length) return

	if (!("IntersectionObserver" in window)) {
		items.forEach(revealNow)
		return
	}

	observer ??= new IntersectionObserver(onIntersect, OBSERVER_OPTIONS)
	items.forEach(el => {
		if (!isPending(el)) return
		if (isDue(el)) {
			revealNow(el)
			return
		}
		observer.observe(el)
	})
}

let paint = (el, progress, shift, blur) => {
	el.style.translate = `0 ${(progress * shift).toFixed(1)}px`
	el.style.filter = progress > 0.001 ? `blur(${(progress * blur).toFixed(1)}px)` : ""
	el.style.opacity = progress > 0.001 ? String(1 - progress) : ""
}

let dissolve = () => {
	dissolveQueued = false
	let sectionProgress = new Map()

	heroItems.forEach(el => {
		let rect = el.getBoundingClientRect()
		let progress = clamp01((HEADER_OFFSET - rect.top) / (rect.height * 0.85))
		sectionProgress.set(el.closest("section") ?? el, progress)
		paint(el, progress, HERO_SHIFT, HERO_BLUR)
	})

	fadeItems.forEach(el => {
		let section = el.closest("section")
		let rect = el.getBoundingClientRect()
		let progress = sectionProgress.has(section)
			? sectionProgress.get(section)
			: clamp01((HEADER_OFFSET - rect.top) / 140)
		paint(el, progress, FADE_SHIFT, FADE_BLUR)
	})
}

let queueDissolve = () => {
	if (dissolveQueued) return
	dissolveQueued = true
	requestAnimationFrame(dissolve)
}

let paintOrbit = orbit => {
	orbit.el.style.setProperty("--bv-orbit-spin", orbit.spin.toFixed(2) + "deg")
	orbit.el.style.setProperty("--bv-orbit-ring-spin", (orbit.spin * ORBIT_RING_RATIO).toFixed(2) + "deg")
}

let stepOrbits = () => {
	orbits.forEach(orbit => {
		if (!orbit.visible) return
		if (orbit.coasting) {
			orbit.velocity *= ORBIT_FRICTION
			if (Math.abs(orbit.velocity) < ORBIT_REST) orbit.velocity = 0
		} else {
			orbit.velocity += (orbit.target - orbit.velocity) * ORBIT_EASE
		}
		if (orbit.velocity === 0) return
		orbit.spin += orbit.velocity
		paintOrbit(orbit)
	})
	orbitFrame = requestAnimationFrame(stepOrbits)
}

let driveOrbits = () => {
	let delta = window.scrollY - orbitLastY
	orbitLastY = window.scrollY
	if (Math.abs(delta) < ORBIT_MIN_SCROLL) return
	orbits.forEach(orbit => {
		orbit.target = delta > 0 ? -ORBIT_DRIVE : ORBIT_DRIVE
		orbit.coasting = false
		clearTimeout(orbit.timer)
		orbit.timer = setTimeout(() => {
			orbit.coasting = true
		}, ORBIT_COAST_DELAY)
	})
}

let collectOrbits = () => {
	orbits.forEach(orbit => clearTimeout(orbit.timer))
	if (orbitWatcher) orbitWatcher.disconnect()

	orbits = [...document.querySelectorAll(ORBIT_SELECTOR)].map(el => ({
		el,
		spin: 0,
		velocity: 0,
		target: 0,
		coasting: true,
		visible: false,
		timer: null
	}))
	if (!orbits.length) return

	orbitWatcher = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			let orbit = orbits.find(item => item.el === entry.target)
			if (orbit) orbit.visible = entry.isIntersecting
		})
	}, ORBIT_OPTIONS)
	orbits.forEach(orbit => orbitWatcher.observe(orbit.el))

	orbitLastY = window.scrollY
	if (!orbitFrame) orbitFrame = requestAnimationFrame(stepOrbits)
}

let collectDissolve = () => {
	heroItems = [...document.querySelectorAll(HERO_SELECTOR)]
	fadeItems = [...document.querySelectorAll(FADE_SELECTOR)]
	if (heroItems.length || fadeItems.length) queueDissolve()
}

let start = () => {
	observe(document)
	window.addEventListener("scroll", scheduleSweep, { passive: true })
	window.addEventListener("resize", scheduleSweep, { passive: true })
	window.addEventListener("pageshow", sweep)

	if (matchMedia("(prefers-reduced-motion: reduce)").matches) return
	motionAllowed = true

	collectDissolve()
	collectOrbits()
	window.addEventListener("scroll", queueDissolve, { passive: true })
	window.addEventListener("resize", queueDissolve, { passive: true })
	window.addEventListener("scroll", driveOrbits, { passive: true })
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", start)
} else {
	start()
}

document.addEventListener("shopify:section:load", event => {
	observe(event.target)
	collectDissolve()
	if (motionAllowed) collectOrbits()
})
